// Baserat på https://react.rocks/example/react-input-calendar
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import cs from 'classnames';
import { format, isAfter, isBefore, isValid, parseISO, startOfToday } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { useI18n } from '../../i18n';
import { DaySelector } from './DaySelector';
import { MonthSelector } from './MonthSelector';
import { YearSelector } from './YearSelector';
const clampDate = (date, min, max)=>{
    if (min && date < min) {
        return min;
    }
    if (max && date > max) {
        return max;
    }
    return date;
};
const getDate = (date)=>{
    if (!date) {
        return null;
    }
    const d = parseISO(date);
    return isValid(d) ? d : null;
};
var View = /*#__PURE__*/ function(View) {
    View[View["Day"] = 0] = "Day";
    View[View["Month"] = 1] = "Month";
    View[View["Year"] = 2] = "Year";
    return View;
}(View || {});
export const Calendar = /*#__PURE__*/ forwardRef(function Calendar(props, ref) {
    const { className, disabled, includeTime, onDatePicked } = props;
    const i18n = useI18n();
    const [currentView, setCurrentView] = useState(0);
    const [visible, setVisible] = useState(false);
    useImperativeHandle(ref, ()=>{
        return {
            open: ()=>setVisible(true),
            close: ()=>setVisible(false),
            isOpen: ()=>visible
        };
    }, [
        visible
    ]);
    const minDate = getDate(props.minDate);
    const maxDate = getDate(props.maxDate);
    const dateFormat = includeTime ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd';
    const [calendarDate, setCalendarDate] = useState(clampDate(getDate(props.date) ?? new Date(), minDate, maxDate));
    useEffect(()=>setCalendarDate(clampDate(getDate(props.date) ?? new Date(), getDate(props.minDate), getDate(props.maxDate))), [
        props.date,
        props.minDate,
        props.maxDate
    ]);
    useEffect(()=>{
        const documentClickHandler = ()=>setVisible(false);
        const keydownHandler = (e)=>{
            switch(e.key){
                case 'Escape':
                case 'Tab':
                    setVisible(false);
                    break;
            }
        };
        if (visible) {
            document.body.click();
            document.addEventListener('click', documentClickHandler);
            document.addEventListener('keydown', keydownHandler);
        }
        return ()=>{
            document.removeEventListener('click', documentClickHandler);
            document.removeEventListener('keydown', keydownHandler);
        };
    }, [
        visible
    ]);
    const prevView = ()=>{
        if (currentView > 0) {
            setCurrentView(currentView - 1);
        }
    };
    const nextView = ()=>{
        if (currentView < 2) {
            setCurrentView(currentView + 1);
        }
    };
    const setDate = (d, closeCalendar)=>{
        if (isValid(d)) {
            setCalendarDate(d);
            if (closeCalendar) {
                onDatePicked(format(d, dateFormat));
                setVisible(false);
            }
        }
    };
    let view;
    switch(currentView){
        case 0:
            view = /*#__PURE__*/ _jsx(DaySelector, {
                date: calendarDate,
                nextView: nextView,
                maxDate: maxDate,
                minDate: minDate,
                setDate: setDate
            });
            break;
        case 1:
            view = /*#__PURE__*/ _jsx(MonthSelector, {
                date: calendarDate,
                nextView: nextView,
                maxDate: maxDate,
                minDate: minDate,
                prevView: prevView,
                setDate: setDate
            });
            break;
        case 2:
            view = /*#__PURE__*/ _jsx(YearSelector, {
                date: calendarDate,
                maxDate: maxDate,
                minDate: minDate,
                prevView: prevView,
                setDate: setDate
            });
            break;
    }
    const calendarIconClick = (e)=>{
        e.stopPropagation();
        if (!disabled) {
            setVisible((v)=>!v);
        }
    };
    const todayClick = ()=>{
        const today = startOfToday();
        const isDisabled = !!minDate && isBefore(today, minDate) || !!maxDate && isAfter(today, maxDate);
        if (!isDisabled) {
            setDate(today, true);
        }
    };
    const todayText = i18n.t('component.calendar.today');
    const calendarClass = 'input-calendar-wrapper';
    const calendar = visible && /*#__PURE__*/ _jsxs("div", {
        className: calendarClass,
        onClick: (e)=>e.stopPropagation(),
        children: [
            view,
            /*#__PURE__*/ _jsx("span", {
                onClick: todayClick,
                className: "today-btn",
                children: todayText
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: cs('input-calendar', className),
        onClick: calendarIconClick,
        children: [
            /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                className: "icon-wrapper calendar-icon",
                icon: faCalendarAlt
            }),
            calendar
        ]
    });
});
export default Calendar;
